import { Match2FrenzyProgression } from '../data/Match2FrenzyProgression.schema';
import { BlockId } from 'src/game/minigame/match2-timestep/game/defs/block';
import { Match2FrenzyTheme } from './frenzy/match2-frenzy.ruleset';

export function getMatch2FrenzyIntroTimestampKey() {
  return `frenzy-intro`;
}

export function getMatch2PlayOnAppCooldownTimestampKey() {
  return `play-on-app-cooldown`;
}

export function getInviteFriendsTimestampKey() {
  return `invite-friends`;
}

export function getMatch2WinLadderResetTimestampKey() {
  return `win-ladder-reset`;
}

export function getSocialContinueCooldownTimestampKey() {
  return `social-continue-cooldown`;
}

export function getMakeHappyCooldownTimestampKey() {
  return `make-happy-cooldown`;
}

// Number of moves to add to the game when using a free continue
export const freeExtraMovesUsedPerContinue = 5;

export const match2Boosters = ['dart', 'bullet', 'drill', 'roulette'] as const;
export type Match2BoosterType = (typeof match2Boosters)[number];
export type Match2Booster = {
  price: number;
} & { type: Match2BoosterType; quantity: number };

export const match2Powerups = ['rocket', 'bomb', 'cube'] as const;
export const match2PowerupsObj = match2Powerups.reduce(
  (acc, next) => ({ ...acc, [next]: null }),
  {} as Record<string, null>,
);

export type Match2PowerupType = (typeof match2Powerups)[number];

export type Match2Powerup = {
  price: number;
  type: Match2PowerupType;
  quantity: number;
};

export const match2UnlockableLevels = {
  // Boosters
  dart: 10,
  bullet: 13,
  drill: 17,
  roulette: 19,

  // Powerups
  rocket: 7,
  bomb: 7,
  cube: 7,

  // Misc
  buruStreak: 12,
  frenzy: 12,
};

export type Match2BuruStreakRewardSet = {
  wins: number;
  rewards: Partial<Record<Match2PowerupType, number>>;
};

export const match2BuruStreakRewards: Match2BuruStreakRewardSet[] = [
  { wins: 1, rewards: { bomb: 1, rocket: 1 } },
  { wins: 2, rewards: { bomb: 2, rocket: 2 } },
  { wins: 3, rewards: { bomb: 3, rocket: 3 } },
];

export type GoalProgressAnalytics = {
  goalProgress1: number;
  goalType1: BlockId;
  goalProgress2?: number;
  goalType2?: BlockId;
  goalProgress3?: number;
  goalType3?: BlockId;
  goalProgress4?: number;
  goalType4?: BlockId;
  goalProgress5?: number;
  goalType5?: BlockId;
  goalProgress6?: number;
  goalType6?: BlockId;
  goalProgress7?: number;
  goalType7?: BlockId;
  goalProgress8?: number;
  goalType8?: BlockId;
  goalProgressAverage: number;
};

export type PuzzleResults = {
  moves: {
    limit: number;
    used: number;
    remaining: number;
  };
  usedContinues: number;
  socialContinueMoves: number;
  adContinueMoves: number;
  continueSpend: number;
  usedBoosters: Partial<Record<Match2BoosterType, number>>;
  boosterSpend: number;
  boosterSpendType: 'Free' | 'Paid' | 'Mixed' | undefined;
  blocksBroken: Partial<Record<BlockId, number>>;
  powerBlocksCreated: Partial<Record<Match2PowerupType, number>>;
  blocksBrokenAnalytics: Record<string, number>;
  totalObstacleGoals: number;
  totalObstaclesRemaining: number;
  usingSaveState?: boolean;
  usedAutoShuffles?: number;
  score?: {
    points: number;
    stars: number;
    percentage: number;
  };
  goalProgressAnalytics?: GoalProgressAnalytics;
} & (
  | {
      result: 'win';
    }
  | { result: 'lose' | 'quit' | 'broken' }
);

export const match2EnergyCost = 1;

/** Bonus moves on successive failed retries of the same level; the final tier repeats. */
export const escalatingContinueRetryBonuses = [0, 5, 10, 15];

// Continues prices based on how many continues the player has already purchased. The last one will be repeated indefinitely.
export const match2Continues = [
  { moves: 5, price: 200 },
  {
    moves: 5,
    powerups: [{ type: 'rocket', amount: 1 }],
    price: 400,
  },
  {
    moves: 5,
    powerups: [
      { type: 'rocket', amount: 1 },
      { type: 'bomb', amount: 1 },
    ],
    price: 600,
  },
  {
    moves: 5,
    powerups: [
      { type: 'rocket', amount: 1 },
      { type: 'bomb', amount: 1 },
      { type: 'cube', amount: 1 },
    ],
    price: 800,
  },
  {
    moves: 5,
    powerups: [
      { type: 'rocket', amount: 2 },
      { type: 'bomb', amount: 2 },
      { type: 'cube', amount: 2 },
    ],
    price: 1000,
  },
];
export const match2BoosterCatalog = {
  dart: {
    quantity: 3,
    price: 400,
  },
  bullet: {
    quantity: 3,
    price: 800,
  },
  drill: {
    quantity: 3,
    price: 800,
  },
  roulette: {
    quantity: 3,
    price: 400,
  },
};

export const match2PowerupCatalog = {
  rocket: {
    quantity: 3,
    price: 200,
  },
  bomb: {
    quantity: 3,
    price: 300,
  },
  cube: {
    quantity: 3,
    price: 400,
  },
};

export const boosterNames = {
  dart: 'hammer',
  bullet: 'airplane',
  drill: 'rocket',
  roulette: 'pinwheel',
} as const;

export const powerupNames = {
  rocket: 'firecracker',
  bomb: 'waterballoon',
  cube: 'lollipop',
} as const;

// Frenzy Ruleset ---------------
export type Match2FrenzyEventConfig = {
  id: string;
  start: Date;
  duration: number;
  theme: Match2FrenzyTheme;
  objectives: BlockId[];
  progression: Match2FrenzyProgression;
};

// Analytics Ruleset ---------------

/**
 * Numbers for level cost calculation, defined on this ticket:
 * https://github.com/play-co/buruburu/issues/2484
 */
export const match2CostAnalytics = {
  rocketCost: 66.67,
  bombCost: 100,
  cubeCost: 133.33,
  dartCost: 133.33, // Hammer
  bulletCost: 266.67, // Airplane
  drillCost: 266.67, // Cup rocket
  rouletteCost: 133.33, // Pinwheel
  continueCost: 200,
  continueCostCap: 1000,
  costFactor: 200,
};

export type Match2ScoringConfig = {
  baseScoreFactor: number;
  blockDestroyed: number;
  unusedMovesScore: number;
  starsPercentage: [number, number, number];
};

export const match2Scoring: Match2ScoringConfig = {
  baseScoreFactor: 8,
  blockDestroyed: 1_000,
  unusedMovesScore: 15_000,
  starsPercentage: [0.3, 0.85, 1.3],
};

/**
 * Max levels present on the game. Different users will have different max levels, but this is the absolute max.
 */
export const match2MaxLevel = 5050;
export const match2MaxLevelChangedAt = new Date('2026-10-12T12:00:00.000Z');

// We only track AppsFlyer events for levels === match2AppsFlyerLevel. This is to avoid sending too many unnecessary events.
export const match2AppsFlyerLevel = 10;
